
import { faArrowsRotate, faChevronDown, faChevronUp, faCircle, faCube, faDatabase, faFilter, faTableCells, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    Row,
    Spinner,
    Table
} from 'react-bootstrap';
import { getcorpsyncstatus, getdsfsynclogs, getmissingorderreason, getsaporderpipeline, getsmartsheetintegration } from "../service/getdata";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// Slugifies a status label into a CSS class suffix, e.g. "In Progress" -> "in-progress".
const slug = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, "-");

const hasData = (d) => d?.data && Object.keys(d.data).length > 0;

const statusDot = (status) => (
    <span className="text-nowrap">
        <FontAwesomeIcon icon={faCircle} className={`od-status-dot od-status--${slug(status)}`} />
        <span className="ps-2">{status}</span>
    </span>
);

const emptyGtsData = {
    widgets: {},
    syncHistory: []
};

const healthPill = (label) => (
    <span className={`rounded-pill od-health-pill od-pill--${slug(label)}`}>
        <FontAwesomeIcon icon={faCircle} className="od-health-dot" />
        <span className="ps-1">{label || "Unknown"}</span>
    </span>
);

const chevron = (open, onClick) => (
    <FontAwesomeIcon
        icon={open ? faChevronUp : faChevronDown}
        className="od-chevron mt-1 ms-2 text-muted"
        onClick={onClick}
    />
);

const widgetSpinner = (
    <div className="text-center py-4">
        <Spinner animation="border" variant="primary" size="sm" />
    </div>
);

const PAGE_SIZE_OPTIONS = [50, 100, 500, 1000];

// Reusable pagination bar driving top (pageSize) and skip (page * pageSize).
const PaginationBar = ({ page, pageSize, totalCount, rowCount, onPageChange, onPageSizeChange }) => {
    const total = totalCount || 0;
    const start = rowCount === 0 ? 0 : page * pageSize + 1;
    const end = page * pageSize + (rowCount || 0);
    // When totalCount is known, use it; otherwise assume more data exists if the
    // current page returned a full batch (so Next can fetch the next 500/1000).
    const hasNext = total > 0 ? end < total : rowCount === pageSize;
    return (
        <div className="d-flex align-items-center justify-content-between flex-wrap mt-2">
            <div className="d-flex align-items-center">
                <span className="od-font-sm me-2">Rows per page</span>
                <Form.Select
                    size="sm"
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="od-hours-input"
                >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Form.Select>
            </div>
            <div className="d-flex align-items-center">
                <span className="od-font-sm me-3">{start}–{end}{total ? ` of ${total}` : ""}</span>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="me-2"
                    disabled={page <= 0}
                    onClick={() => onPageChange(page - 1)}
                >
                    Prev
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!hasNext}
                    onClick={() => onPageChange(page + 1)}
                >
                    Next
                </Button>
            </div>
        </div>
    );
};

const Tile = ({ label, value, sub, tone }) => (
    <Card className="h-100 shadow-sm">
        <Card.Body className="p-3">
            <div className="text-muted text-uppercase od-tile-label">{label}</div>
            <div className={`fw-bold mt-1 od-tile-value${tone ? ` od-status--${slug(tone)}` : ""}`}>{value}</div>
            {sub ? <div className="text-muted od-tile-sub">{sub}</div> : null}
        </Card.Body>
    </Card>
);

// Data comes from GET /saporderpipelinelogs.
const emptySapData = {
    totalCount: 0,
    widgets: {},
    data: []
};

const tiles = (data) => {
    const w = data?.widgets || {};
    return [
        { label: "Pipeline Status", value: w.PipelineStatus || "—", tone: w.PipelineStatus },
        { label: "Orders Received", value: w.OrdersReceived != null ? fmt(w.OrdersReceived) : "—" },
        { label: "Orders Loaded", value: w.OrdersLoaded != null ? fmt(w.OrdersLoaded) : "—", tone: "success" },
        { label: "Pipeline Failed", value: w.PipeLineFailed != null ? fmt(w.PipeLineFailed) : "—", tone: "failed" },
        { label: "Orders Rejected", value: w.OrdersRejected != null ? fmt(w.OrdersRejected) : "—", tone: "failed" },
        { label: "Last Run Time", value: fmtTime(w.LastRunTime) }
    ];
};

// Data comes from GET /smartsheetwebhooklogs.
const emptySmartsheetData = {
    totalCount: 0,
    widgets: {},
    data: []
};

const smartsheettiles = (data) => {
    const w = data?.widgets || {};
    return [
        { label: "Current Status", value: w.CurrentStatus || "—", tone: w.CurrentStatus || "partial-success" },
        { label: "Records Synced", value: w.RecordsSynced != null ? fmt(w.RecordsSynced) : "—" },
        { label: "Records Failed", value: w.RecordsFailed != null ? fmt(w.RecordsFailed) : "—", tone: "failed" },
        { label: "Last Run Time", value: fmtTime(w.LastRunTime) }
    ];
};

const gtstiles = (data) => {
    const w = data?.widgets || {};
    return [
        { label: "Current Status", value: w.CurrentStatus || "—", tone: w.CurrentStatus, sub: w.CurrentStatusEntity || undefined },
        { label: "Last Sync Status", value: w.LastSyncStatus || "—", tone: w.LastSyncStatus, sub: w.LastSyncStatusEntity || undefined },
        { label: "Records Synced", value: w.TotalRecordsSynced != null ? fmt(w.TotalRecordsSynced) : "—" },
        { label: "Records Failed", value: w.TotalRecordsFailed != null ? fmt(w.TotalRecordsFailed) : "—", tone: "failed" },
        { label: "Avg Sync Time", value: w.AvgSyncTimeSecs != null ? `${w.AvgSyncTimeSecs} sec` : "—" }
    ];
};

// Data comes from GET /orderreasonalertlog.
const emptyMissingData = {
    totalCount: 0,
    data: []
};

// Data comes from GET /dsfsynclogs (Druid "pilogs" datasource).
const emptyDsfData = {
    totalCount: 0,
    widgets: {},
    data: []
};

const dsftiles = (data) => {
    const w = data?.widgets || {};
    return [
        { label: "Status", value: w.CurrentStatus || "—", tone: w.CurrentStatus },
        { label: "Total Events", value: w.TotalEvents != null ? fmt(w.TotalEvents) : "—" },
        { label: "Errors", value: w.ErrorCount != null ? fmt(w.ErrorCount) : "—", tone: "failed" },
        { label: "Failing Jobs", value: w.FailingJobs != null ? fmt(w.FailingJobs) : "—" },
        { label: "Last Event Time", value: fmtTime(w.LastEventTime) }
    ];
};

function DistributionSnapshotPS() {
    const [pipelinedata, setpipelinedata] = useState(emptySapData);
    const [smartsheetdata, setsmartsheetdata] = useState(emptySmartsheetData);
    const [gtsdata, setgtsdata] = useState(emptyGtsData);
    const [missingorderdata, setmissingorderdata] = useState(emptyMissingData);
    const [dsfdata, setdsfdata] = useState(emptyDsfData);
    const [loadingsap, setloadingsap] = useState(true);
    const [loadingsmartsheet, setloadingsmartsheet] = useState(true);
    const [loadinggts, setloadinggts] = useState(true);
    const [loadingmissingorder, setloadingmissingorder] = useState(true);
    const [loadingdsf, setloadingdsf] = useState(true);
    const [showsummary, setshowsummary] = useState(true);
    const [showsmartsheet, setshowsmartsheet] = useState(true);
    const [showgts, setshowgts] = useState(true);
    const [showmissingorder, setshowmissingorder] = useState(true);
    const [showdsf, setshowdsf] = useState(true);
    const [failurehours, setfailurehours] = useState(24);
    const [smartsheethours, setsmartsheethours] = useState(24);
    const [gtssynchours, setgtssynchours] = useState(24);
    const [dsfhours, setdsfhours] = useState(24);
    const [sappage, setsappage] = useState(0);
    const [sappagesize, setsappagesize] = useState(50);
    const [smartsheetpage, setsmartsheetpage] = useState(0);
    const [smartsheetpagesize, setsmartsheetpagesize] = useState(50);
    const [missingpage, setmissingpage] = useState(0);
    const [missingpagesize, setmissingpagesize] = useState(50);
    const [missinghours, setmissinghours] = useState(24);
    const [dsfpage, setdsfpage] = useState(0);
    const [dsfpagesize, setdsfpagesize] = useState(50);

    const loaddata = useCallback((hours, top, skip) => {
        setloadingsap(true);
        getsaporderpipeline(top, skip, hours)
            .then((d) => {
                if (hasData(d)) {
                    setpipelinedata(d.data);
                } else {
                    setpipelinedata(emptySapData);
                }
            })
            .catch(() => {
                setpipelinedata(emptySapData);
            })
            .finally(() => setloadingsap(false));
    }, []);

    const loadsmartsheet = useCallback((hours, top, skip) => {
        setloadingsmartsheet(true);
        getsmartsheetintegration(top, skip, hours)
            .then((d) => {
                if (hasData(d)) {
                    setsmartsheetdata(d.data);
                } else {
                    setsmartsheetdata(emptySmartsheetData);
                }
            })
            .catch(() => {
                setsmartsheetdata(emptySmartsheetData);
            })
            .finally(() => setloadingsmartsheet(false));
    }, []);

    const loadgts = useCallback((hours) => {
        setloadinggts(true);
        getcorpsyncstatus(hours)
            .then((d) => {
                if (hasData(d)) {
                    setgtsdata(d.data);
                } else {
                    setgtsdata(emptyGtsData);
                }
            })
            .catch(() => {
                setgtsdata(emptyGtsData);
            })
            .finally(() => setloadinggts(false));
    }, []);

    const loadmissingorder = useCallback((hours, top, skip) => {
        setloadingmissingorder(true);
        getmissingorderreason(top, skip, hours)
            .then((d) => {
                if (hasData(d)) {
                    setmissingorderdata(d.data);
                } else {
                    setmissingorderdata(emptyMissingData);
                }
            })
            .catch(() => {
                setmissingorderdata(emptyMissingData);
            })
            .finally(() => setloadingmissingorder(false));
    }, []);

    const loaddsf = useCallback((hours, top, skip) => {
        setloadingdsf(true);
        getdsfsynclogs(top, skip, hours)
            .then((d) => {
                if (hasData(d)) {
                    setdsfdata(d.data);
                } else {
                    setdsfdata(emptyDsfData);
                }
            })
            .catch(() => {
                setdsfdata(emptyDsfData);
            })
            .finally(() => setloadingdsf(false));
    }, []);

    // Load all widget data once on initial page mount. Filter inputs no longer
    // trigger API calls on change — data is refreshed only via the Filter buttons.
    useEffect(() => {
        loaddata(failurehours, sappagesize, 0);
        loadsmartsheet(smartsheethours, smartsheetpagesize, 0);
        loadgts(gtssynchours);
        loadmissingorder(missinghours, missingpagesize, 0);
        loaddsf(dsfhours, dsfpagesize, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // SAP pagination + filter handlers.
    const applysapfilter = () => {
        setsappage(0);
        loaddata(failurehours, sappagesize, 0);
    };
    const handlesappagechange = (newPage) => {
        setsappage(newPage);
        loaddata(failurehours, sappagesize, newPage * sappagesize);
    };
    const handlesappagesizechange = (size) => {
        setsappagesize(size);
        setsappage(0);
        loaddata(failurehours, size, 0);
    };

    // Smartsheet pagination + filter handlers.
    const applysmartsheetfilter = () => {
        setsmartsheetpage(0);
        loadsmartsheet(smartsheethours, smartsheetpagesize, 0);
    };
    const handlesmartsheetpagechange = (newPage) => {
        setsmartsheetpage(newPage);
        loadsmartsheet(smartsheethours, smartsheetpagesize, newPage * smartsheetpagesize);
    };
    const handlesmartsheetpagesizechange = (size) => {
        setsmartsheetpagesize(size);
        setsmartsheetpage(0);
        loadsmartsheet(smartsheethours, size, 0);
    };

    // Missing order pagination + filter handlers.
    const applymissingfilter = () => {
        setmissingpage(0);
        loadmissingorder(missinghours, missingpagesize, 0);
    };
    const handlemissingpagechange = (newPage) => {
        setmissingpage(newPage);
        loadmissingorder(missinghours, missingpagesize, newPage * missingpagesize);
    };
    const handlemissingpagesizechange = (size) => {
        setmissingpagesize(size);
        setmissingpage(0);
        loadmissingorder(missinghours, size, 0);
    };

    // DSF pagination + filter handlers.
    const applydsffilter = () => {
        setdsfpage(0);
        loaddsf(dsfhours, dsfpagesize, 0);
    };
    const handledsfpagechange = (newPage) => {
        setdsfpage(newPage);
        loaddsf(dsfhours, dsfpagesize, newPage * dsfpagesize);
    };
    const handledsfpagesizechange = (size) => {
        setdsfpagesize(size);
        setdsfpage(0);
        loaddsf(dsfhours, size, 0);
    };

    const history = pipelinedata?.data || [];
    const failureCount = history.filter((r) => r.Status === "Failed").length;

    const syncHistory = smartsheetdata?.data || [];
    const runCount = smartsheetdata?.totalCount ?? syncHistory.length;

    const gtsHistory = gtsdata?.syncHistory || [];

    const missingOrders = missingorderdata?.data || [];
    const pendingCount = missingorderdata?.totalCount ?? missingOrders.length;

    const dsfEvents = dsfdata?.data || [];

    return (
        <>
            <Row className="m-1">
                <Col>
                    <Card>
                        <Card.Header>
                            <FontAwesomeIcon icon={faCube} className="text-primary" />
                            <span className="ps-2 d-inline-block align-top">
                                <strong>SAP Order Pipeline</strong>
                                <span className="d-block text-muted od-font-sm">Orders are being correctly delivered to PS portal</span>
                            </span>
                            {chevron(showsummary, () => setshowsummary(!showsummary))}
                            {healthPill(pipelinedata?.widgets?.PipelineStatus)}
                        </Card.Header>
                        {showsummary ?
                            <Card.Body className="p-2 od-widget-body">
                                <Row>
                                    {tiles(pipelinedata).map((tile, id) => (
                                        <Col key={id} xs={12} sm={6} md={4} lg={true} className="mb-2">
                                            <Tile {...tile} />
                                        </Col>
                                    ))}
                                </Row>

                                <Form className="d-flex align-items-center flex-wrap my-2" onSubmit={(e) => { e.preventDefault(); applysapfilter(); }}>
                                    <FontAwesomeIcon icon={faFilter} className="text-muted me-2" />
                                    <Form.Label className="od-font-sm mb-0 me-2">Show failures in the last</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        min={1}
                                        value={failurehours}
                                        onChange={(e) => setfailurehours(e.target.value)}
                                        className="me-2 od-hours-input"
                                    />
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={applysapfilter}
                                    >
                                        Filter
                                    </Button>
                                    <span className="od-font-sm">
                                        hours — <strong>{failureCount}</strong> failures
                                    </span>
                                </Form>

                                {loadingsap ? widgetSpinner : (
                                <Table hover responsive size="sm" className="od-font-sm align-middle mb-0 border rounded">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="small">RUN TIME</th>
                                            <th className="small">STATUS</th>
                                            <th className="small">DURATION (MINS)</th>
                                            <th className="small">RECORDS PROCESSED</th>
                                            <th className="small">ORDERS NOT CAPTURED</th>
                                            <th className="small">ORDERS REJECTED</th>
                                            <th className="small">ERROR MESSAGE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.length > 0 ?
                                            history.map((row, id) => (
                                                <tr key={id}>
                                                    <td className="small text-nowrap">{fmtTime(row.RunTime)}</td>
                                                    <td className="small">{statusDot(row.Status)}</td>
                                                    <td className="small">{row.DurationMins ?? "—"}</td>
                                                    <td className="small">{row.RecordsProcessed ?? "—"}</td>
                                                    <td className={`small${row.OrdersNotCaptured > 0 ? " text-danger" : ""}`}>{row.OrdersNotCaptured ?? "—"}</td>
                                                    <td className={`small${row.OrdersRejected > 0 ? " text-danger" : ""}`}>{row.OrdersRejected ?? "—"}</td>
                                                    <td className="small text-danger">{row.ErrorMessage || "—"}</td>
                                                </tr>
                                            ))
                                            :
                                            <tr>
                                                <td colSpan={7} className="small text-center text-muted">No execution history available</td>
                                            </tr>
                                        }
                                    </tbody>
                                </Table>
                                )}
                                <PaginationBar
                                    page={sappage}
                                    pageSize={sappagesize}
                                    totalCount={pipelinedata?.totalCount}
                                    rowCount={history.length}
                                    onPageChange={handlesappagechange}
                                    onPageSizeChange={handlesappagesizechange}
                                />
                            </Card.Body>
                            : <></>}
                    </Card>
                </Col>
            </Row>

            <Row className="m-1">
                <Col>
                    <Card>
                        <Card.Header>
                            <FontAwesomeIcon icon={faTableCells} className="text-primary" />
                            <span className="ps-2 d-inline-block align-top">
                                <strong>Smartsheet Integration</strong>
                                <span className="d-block text-muted od-font-sm">GO Live data syncing correctly to PS portal</span>
                            </span>
                            {chevron(showsmartsheet, () => setshowsmartsheet(!showsmartsheet))}
                            {healthPill(smartsheetdata?.widgets?.CurrentStatus)}
                        </Card.Header>
                        {showsmartsheet ?
                            <Card.Body className="p-2 od-widget-body">
                                <Row>
                                    {smartsheettiles(smartsheetdata).map((tile, id) => (
                                        <Col key={id} xs={12} sm={6} md={3} className="mb-2">
                                            <Tile {...tile} />
                                        </Col>
                                    ))}
                                </Row>

                                <Form className="d-flex align-items-center flex-wrap my-2" onSubmit={(e) => { e.preventDefault(); applysmartsheetfilter(); }}>
                                    <FontAwesomeIcon icon={faFilter} className="text-muted me-2" />
                                    <Form.Label className="od-font-sm mb-0 me-2">Runs in the last</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        min={1}
                                        value={smartsheethours}
                                        onChange={(e) => setsmartsheethours(e.target.value)}
                                        className="me-2 od-hours-input"
                                    />
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={applysmartsheetfilter}
                                    >
                                        Filter
                                    </Button>
                                    <span className="od-font-sm">
                                        hours — <strong>{runCount}</strong> runs
                                    </span>
                                </Form>

                                {loadingsmartsheet ? widgetSpinner : (
                                <Table hover responsive size="sm" className="od-font-sm align-middle mb-0 border rounded">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="small">RUN TIME</th>
                                            <th className="small">STATUS</th>
                                            <th className="small">PORTAL</th>
                                            <th className="small">ROWS SYNCED</th>
                                            <th className="small">ROWS FAILED</th>
                                            <th className="small">DURATION (MINS)</th>
                                            <th className="small">FAILURE REASON</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {syncHistory.length > 0 ?
                                            syncHistory.map((row, id) => (
                                                <tr key={id}>
                                                    <td className="small text-nowrap">{fmtTime(row.RunTime)}</td>
                                                    <td className="small">{statusDot(row.Status)}</td>
                                                    <td className="small">{row.Portal}</td>
                                                    <td className="small">{row.RowsSynced ?? "—"}</td>
                                                    <td className="small">{row.RowsFailed ?? "—"}</td>
                                                    <td className="small">{row.DurationMins ?? "—"}</td>
                                                    <td className="small text-danger">{row.FailureReason || "—"}</td>
                                                </tr>
                                            ))
                                            :
                                            <tr>
                                                <td colSpan={7} className="small text-center text-muted">No sync history available</td>
                                            </tr>
                                        }
                                    </tbody>
                                </Table>
                                )}
                                <PaginationBar
                                    page={smartsheetpage}
                                    pageSize={smartsheetpagesize}
                                    totalCount={smartsheetdata?.totalCount}
                                    rowCount={syncHistory.length}
                                    onPageChange={handlesmartsheetpagechange}
                                    onPageSizeChange={handlesmartsheetpagesizechange}
                                />
                            </Card.Body>
                            : <></>}
                    </Card>
                </Col>
            </Row>

            <Row className="m-1">
                <Col>
                    <Card>
                        <Card.Header>
                            <FontAwesomeIcon icon={faArrowsRotate} className="text-success" />
                            <span className="ps-2 d-inline-block align-top">
                                <strong>Sync with GTS</strong>
                                <span className="d-block text-muted od-font-sm">GTS API reporting data and sync occurring correctly</span>
                            </span>
                            {chevron(showgts, () => setshowgts(!showgts))}
                            {healthPill(gtsdata?.widgets?.CurrentStatus)}
                        </Card.Header>
                        {showgts ?
                            <Card.Body className="p-2 od-widget-body">
                                <Row>
                                    {gtstiles(gtsdata).map((tile, id) => (
                                        <Col key={id} xs={12} sm={6} md={4} lg={true} className="mb-2">
                                            <Tile {...tile} />
                                        </Col>
                                    ))}
                                </Row>

                                <Form className="d-flex align-items-center flex-wrap my-2" onSubmit={(e) => { e.preventDefault(); loadgts(gtssynchours); }}>
                                    <FontAwesomeIcon icon={faFilter} className="text-muted me-2" />
                                    <Form.Label className="od-font-sm mb-0 me-2">Show sync results in the last</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        min={1}
                                        value={gtssynchours}
                                        onChange={(e) => setgtssynchours(e.target.value)}
                                        className="me-2 od-hours-input"
                                    />
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => loadgts(gtssynchours)}
                                    >
                                        Filter
                                    </Button>
                                    <span className="od-font-sm">hours</span>
                                </Form>

                                {loadinggts ? widgetSpinner : (
                                <Table hover responsive size="sm" className="od-font-sm align-middle mb-0 border rounded">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="small od-col-14">LAST SYNC TIMESTAMP</th>
                                            <th className="small od-col-14">ENTITY</th>
                                            <th className="small od-col-14">STATUS</th>
                                            <th className="small od-col-14">RECORDS SYNCED</th>
                                            <th className="small od-col-14">RECORDS FAILED</th>
                                            <th className="small od-col-30">FAILURE REASON</th>
                                            <th className="small od-col-14">TIME TO SYNC (SECS)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gtsHistory.length > 0 ?
                                            gtsHistory.map((row, id) => (
                                                <tr key={id}>
                                                    <td className="small text-nowrap od-col-14">{fmtTime(row.LastSyncEndTimestamp)}</td>
                                                    <td className="small od-col-14">{row.Entity ?? "—"}</td>
                                                    <td className="small od-col-14">{statusDot(row.CurrentStatus)}</td>
                                                    <td className="small od-col-14">{row.RecordsSynced ?? "—"}</td>
                                                    <td className="small od-col-14">{row.RecordsFailed ?? "—"}</td>
                                                    <td className="small text-danger od-col-30">{row.FailureReasons || "—"}</td>
                                                    <td className="small od-col-14">{row.LatestBatchAvgSyncSecs ?? "—"}</td>
                                                </tr>
                                            ))
                                            :
                                            <tr>
                                                <td colSpan={7} className="small text-center text-muted">No sync history available</td>
                                            </tr>
                                        }
                                    </tbody>
                                </Table>
                                )}
                            </Card.Body>
                            : <></>}
                    </Card>
                </Col>
            </Row>

            <Row className="m-1">
                <Col>
                    <Card>
                        <Card.Header>
                            <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
                            <span className="ps-2 d-inline-block align-top">
                                <strong>Missing Order Reason</strong>
                                <span className="d-block text-muted od-font-sm">Orders without a reason — pending update from OM team via SAP</span>
                            </span>
                            {chevron(showmissingorder, () => setshowmissingorder(!showmissingorder))}
                            <Badge pill bg="warning" text="dark" className="me-2 float-end">
                                {pendingCount} pending
                            </Badge>
                        </Card.Header>
                        {showmissingorder ?
                            <Card.Body className="p-2 od-widget-body">
                                <Form className="d-flex align-items-center flex-wrap my-2" onSubmit={(e) => { e.preventDefault(); applymissingfilter(); }}>
                                    <FontAwesomeIcon icon={faFilter} className="text-muted me-2" />
                                    <Form.Label className="od-font-sm mb-0 me-2">Show orders in the last</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        min={1}
                                        value={missinghours}
                                        onChange={(e) => setmissinghours(e.target.value)}
                                        className="me-2 od-hours-input"
                                    />
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={applymissingfilter}
                                    >
                                        Filter
                                    </Button>
                                    <span className="od-font-sm">hours</span>
                                </Form>

                                {loadingmissingorder ? widgetSpinner : (
                                <Table hover responsive size="sm" className="od-font-sm align-middle mb-0 border rounded">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="small">ORDER #</th>
                                            <th className="small">SAP CUSTOMER #</th>
                                            <th className="small">CUSTOMER NAME</th>
                                            <th className="small">ORDER MANAGER</th>
                                            <th className="small">API RUN TIME</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {missingOrders.length > 0 ?
                                            missingOrders.map((row, id) => (
                                                <tr key={id}>
                                                    <td className="small"><a href={`#${row.SalesOrderNumber}`}>{row.SalesOrderNumber}</a></td>
                                                    <td className="small">{row.SAPCustomerNumber}</td>
                                                    <td className="small">{row.CustomerName}</td>
                                                    <td className="small">{row.OrderManagerName || "—"}</td>
                                                    <td className="small text-nowrap">{fmtTime(row.AddedOn)}</td>
                                                </tr>
                                            ))
                                            :
                                            <tr>
                                                <td colSpan={5} className="small text-center text-muted">No pending orders</td>
                                            </tr>
                                        }
                                    </tbody>
                                </Table>
                                )}
                                <PaginationBar
                                    page={missingpage}
                                    pageSize={missingpagesize}
                                    totalCount={missingorderdata?.totalCount}
                                    rowCount={missingOrders.length}
                                    onPageChange={handlemissingpagechange}
                                    onPageSizeChange={handlemissingpagesizechange}
                                />
                            </Card.Body>
                            : <></>}
                    </Card>
                </Col>
            </Row>

            <Row className="m-1">
                <Col>
                    <Card>
                        <Card.Header>
                            <FontAwesomeIcon icon={faDatabase} className="text-primary" />
                            <span className="ps-2 d-inline-block align-top">
                                <strong>Raymond &rarr; PS Entity Data CDC Pipeline</strong>
                                <span className="d-block text-muted od-font-sm">CDC inserting entity data to Project Services tables</span>
                            </span>
                            {chevron(showdsf, () => setshowdsf(!showdsf))}
                            {healthPill(dsfdata?.widgets?.CurrentStatus)}
                        </Card.Header>
                        {showdsf ?
                            <Card.Body className="p-2 od-widget-body">
                                <Row>
                                    {dsftiles(dsfdata).map((tile, id) => (
                                        <Col key={id} xs={12} sm={6} md={4} lg={true} className="mb-2">
                                            <Tile {...tile} />
                                        </Col>
                                    ))}
                                </Row>

                                <Form className="d-flex align-items-center flex-wrap my-2" onSubmit={(e) => { e.preventDefault(); applydsffilter(); }}>
                                    <FontAwesomeIcon icon={faFilter} className="text-muted me-2" />
                                    <Form.Label className="od-font-sm mb-0 me-2">Show events in the last</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        min={1}
                                        value={dsfhours}
                                        onChange={(e) => setdsfhours(e.target.value)}
                                        className="me-2 od-hours-input"
                                    />
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={applydsffilter}
                                    >
                                        Filter
                                    </Button>
                                    <span className="od-font-sm">hours</span>
                                </Form>

                                {loadingdsf ? widgetSpinner : (
                                <Table hover responsive size="sm" className="od-font-sm align-middle mb-0 border rounded">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="small">TIME</th>
                                            <th className="small">JOB (SC)</th>
                                            <th className="small">LEVEL</th>
                                            <th className="small">COMPONENT</th>
                                            <th className="small">CODE</th>
                                            <th className="small">MESSAGE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dsfEvents.length > 0 ?
                                            dsfEvents.map((row, id) => (
                                                <tr key={id}>
                                                    <td className="small text-nowrap">{fmtTime(row.__time)}</td>
                                                    <td className="small">{row.SC || "—"}</td>
                                                    <td className="small">{statusDot(row.T)}</td>
                                                    <td className="small">{row.C || "—"}</td>
                                                    <td className="small">{row.CD ?? "—"}</td>
                                                    <td className="small text-danger">{row.M || "—"}</td>
                                                </tr>
                                            ))
                                            :
                                            <tr>
                                                <td colSpan={6} className="small text-center text-muted">No log events found</td>
                                            </tr>
                                        }
                                    </tbody>
                                </Table>
                                )}
                                <PaginationBar
                                    page={dsfpage}
                                    pageSize={dsfpagesize}
                                    totalCount={dsfdata?.totalCount}
                                    rowCount={dsfEvents.length}
                                    onPageChange={handledsfpagechange}
                                    onPageSizeChange={handledsfpagesizechange}
                                />
                            </Card.Body>
                            : <></>}
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default DistributionSnapshotPS;